import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, MenuItem, Select, Stack, Typography } from '@mui/material';
import { KinesisVideoStreamContext } from '../../VideoStream/Context/KinesisVideoStreamContext';
import { useContext, useEffect, useMemo, useReducer } from 'react';
import { useKVStreams } from '../../VideoStream/common/useKVStreams';
import { AWS_KVS_Player, HLS_Player } from '../../VideoStream/common/player';
import { HistoryStoreContext } from '../../../ContextProviders/HistoryStoreContext';
import { ObservationsContext } from '../Contexts/OvesrvationsContextType_V2';
import { VideoHub, VideoStream, } from '@iotv/datamodel-core';
import { Environment } from 'internal-config/Environment';
import { KinesisVideoClient } from '@aws-sdk/client-kinesis-video';
import { Auth } from 'aws-amplify';
import { BinaryChoiceRadio } from '../../common/BinaryChoice';
import { useLinkedTable } from './useLinkedTable';
import { useFragmentDetector } from '../../VideoStream/common/useFragmentDetector';
import { isNonNaNNumber, Milliseconds } from '@iotv/iotv-v3-types';
const checkIsLiveIntervalInMs = process.env.NODE_ENV === 'development' ? Milliseconds.MINUTE * 1 : Milliseconds.SECOND * 15;
const defaultVideoMonitoringHubState = {
    selectedVideoStreamSk: null,
    layout: 'B',
};
const VideoMonitoringTabReducer = (prev, next) => ({ ...prev, ...next });
export const VideoMonitoringTab = (props) => {
    console.log(`VideoMonitoringTab rerender`);
    const { state, dispatch } = useContext(KinesisVideoStreamContext);
    const { fromTimestamp, toTimestamp } = state;
    const [localState, localDispatch] = useReducer(VideoMonitoringTabReducer, defaultVideoMonitoringHubState);
    const { selectedVideoStreamSk } = localState;
    const { streamName } = useKVStreams({});
    const { LiveVideoIndicator } = useFragmentDetector({ checkIsLiveIntervalInMs });
    const { LinkedTable, DatasourceSelector, selectedStoreName } = useLinkedTable({ fillHeight: localState.layout === 'B' });
    console.log(`VideoMonitorTabInternal streamName`, streamName);
    const { parameters: {}, } = useContext(HistoryStoreContext);
    const { state: { matchedVideoHubContexts }, } = useContext(ObservationsContext);
    // useEffect(() => {
    //   const { fromTimestamp: stateFromTimestamp, toTimestamp: stateToTimestamp } = state
    //   if (stateFromTimestamp !== fromTimestamp || stateToTimestamp !== toTimestamp) {
    //     const maxPeriod = Milliseconds.DAY - Milliseconds.HOUR
    //     if (toTimestamp - fromTimestamp < maxPeriod) {
    //       dispatch({ fromTimestamp, toTimestamp })
    //     } else {
    //       dispatch({ fromTimestamp: toTimestamp - maxPeriod, toTimestamp })
    //     }
    //   }
    // }, [dispatch, fromTimestamp, state.fromTimestamp, state.toTimestamp, toTimestamp])
    useEffect(() => {
        if (!state.kvClient) {
            console.log(`VideoMonitoringTab initializing kvClient`);
            const setKVClient = async () => {
                const credentials = await Auth.currentUserCredentials();
                const kvClient = new KinesisVideoClient({ region: Environment.region, credentials });
                console.log('KinesisVideoClient initialized:', kvClient);
                dispatch({ kvClient });
            };
            setKVClient();
        }
    }, []);
    const mutedPlayerEventFns = useMemo(() => {
        const noop = () => { };
        return {
            onError: noop,
            onAbort: noop,
            onStalled: noop,
            onSuspend: noop,
            onWaiting: noop,
            onEmptied: noop,
            onCanPlay: noop,
            onCanPlayThrough: noop,
            onTimeUpdate: noop,
        };
    }, []);
    const setSelectedStream = (selectedVideoStreamSk) => {
        if (matchedVideoHubContexts) {
            if (typeof selectedVideoStreamSk === 'string') {
                localDispatch({ selectedVideoStreamSk });
                const videoHubContext = matchedVideoHubContexts[selectedVideoStreamSk];
                const videoHub = videoHubContext.getNodesOfType('VideoHub')?.[0];
                const videoStreams = videoHubContext.getNodesOfType('VideoStream');
                if (videoStreams.length > 1) {
                    console.log('Multiple VideoStreams found for the VideoHub. Using the first one.');
                }
                else {
                    console.log(`Single VideoStream ${videoStreams?.[0]?.name} found for the VideoHub.`);
                }
                const videoStream = videoStreams?.[0];
                if (videoHub && videoStream) {
                    dispatch({ VideoHub: new VideoHub(videoHub), VideoStream: new VideoStream(videoStream) });
                }
                else {
                    console.log(`VideoHub or VideoStream not found for selectedId ${selectedVideoStreamSk}`, { videoHub, videoStream, matchedVideoHubContexts });
                }
            }
            else {
                console.log(`Invalid selectedId`, selectedVideoStreamSk);
            }
        }
    };
    useEffect(() => {
        console.log(`VideoMonitoringTab localState changed`, localState);
        if (matchedVideoHubContexts && Object.keys(matchedVideoHubContexts).length > 0 && !selectedVideoStreamSk) {
            const firstStreamSk = matchedVideoHubContexts[Object.keys(matchedVideoHubContexts)[0]].getNodesOfType('VideoStream')?.[0]?.sk;
            setSelectedStream(firstStreamSk);
        }
    }, [matchedVideoHubContexts]);
    const handleStreamSelect = (e) => {
        const selectedId = e.target.value;
        if (typeof selectedId === 'string') {
            setSelectedStream(selectedId);
        }
    };
    const VideoStreamSelector = useMemo(() => matchedVideoHubContexts ? (_jsx(Select, { size: 'small',
        sx: { minWidth: 300 },
        value: selectedVideoStreamSk ?? 'Select a video stream',
        onChange: handleStreamSelect, children: Object.keys(matchedVideoHubContexts)?.map((context) => {
            const videoStream = matchedVideoHubContexts[context].getNodesOfType('VideoStream')?.[0];
            return (videoStream && (_jsx(MenuItem, { value: videoStream.sk, sx: {}, children: videoStream.name }, videoStream.sk)));
        }) })) : (_jsx(Alert, { severity: "info", sx: { width: '100%' }, children: `No video streams associated with this thing.` })), [dispatch, matchedVideoHubContexts, selectedVideoStreamSk]);
    const TimeRangeDisplay = isNonNaNNumber(fromTimestamp) && isNonNaNNumber(toTimestamp) ? (_jsx(Typography, { children: `Time Range: ${new Date(fromTimestamp).toLocaleString()} to ${new Date(toTimestamp).toLocaleString()}` })) : (_jsx(Alert, { severity: "warning", children: `Time Range Not Set: select a ${selectedStoreName === 'Readings' ? 'Reading' : 'Observation'}` }));
    const stackWidth = localState.layout === 'A' ? '100%' : '50%';
    return (_jsxs(Stack, { direction: 'column', spacing: 2, alignItems: 'flex-start', children: [_jsxs(Stack, { direction: 'row', spacing: 2, alignItems: 'flex-start', width: '100%', children: [_jsx(BinaryChoiceRadio, { currentValue: localState.layout,
                        onChange: (value) => localDispatch({ layout: value.target.value }),
                        valueA: 'A',
                        valueB: 'B',
                        labelA: 'Vertical',
                        labelB: 'Horizontal' }), DatasourceSelector, TimeRangeDisplay] }), _jsxs(Stack, { direction: localState.layout === 'A' ? 'column' : 'row',
                spacing: 2,
                alignItems: localState.layout === 'B' ? 'stretch' : 'flex-start',
                width: '100%', children: [_jsx(Stack, { direction: 'column', spacing: 2, alignItems: 'stretch', width: stackWidth, children: LinkedTable }), _jsxs(Stack, { direction: 'column', spacing: 2, alignItems: 'flex-start', justifyContent: 'flex-start', width: stackWidth, children: [_jsxs(Stack, { direction: 'row', spacing: 2, alignItems: 'center', width: '100%', children: [_jsxs(Stack, { direction: 'row', spacing: 2, alignItems: 'flex-start', children: [LiveVideoIndicator, _jsx(Typography, { children: "Video Stream:" })] }), _jsx(Stack, { direction: 'row', spacing: 2, alignItems: 'flex-start', children: _jsx(Stack, { direction: 'column', spacing: 2, alignItems: 'flex-start', children: VideoStreamSelector }) })] }), selectedVideoStreamSk ? (_jsx(AWS_KVS_Player, { playerEventFns: mutedPlayerEventFns, PlayerComponent: HLS_Player })) : (_jsx(Alert, { severity: "info", sx: { width: '100%' }, children: `No video streams selected.` }))] })] })] }));
};
